import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { selectUserInformations } from "../../features/authSlice";
import { API } from "../../shared/api/Api";
import { showToast, hasPermission } from "../../utils/utils";
import { formatPhoneNumberForDisplay } from "../../utils/phoneUtils";
import Pagination from "../../components/reusable/Pagination";
import Loader from "../../components/Live/Loader";
import { 
  FiShield, 
  FiShieldOff, 
  FiRefreshCw, 
  FiSearch,
  FiUser,
  FiMail,
  FiCalendar,
  FiAlertTriangle,
  FiArrowLeft
} from "react-icons/fi";
import { Link } from "react-router-dom";

const DEBOUNCE_DELAY = 700;

const BlacklistPage = () => {
  const [blacklistedUsers, setBlacklistedUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loader, setLoader] = useState(true);
  const [unblockingId, setUnblockingId] = useState(null);

  const user = useSelector(selectUserInformations);

  const fetchBlacklistedUsers = useCallback(async () => {
    setLoader(true);
    try {
      const { data } = await axios.get(API.Users.app_blacklist_all, {
        params: {
          page: currentPage,
          search: debouncedSearch,
        },
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });

      if (data.status === 200 && data.errorCode === 0) {
        setBlacklistedUsers(data.data.items || []);
        setCurrentPage(data.data.current_page || 1);
        setTotalPages(data.data.last_page || 1);
      } else {
        showToast("Error", data.message || "Failed to fetch blacklisted users", "error");
      }
    } catch (error) {
      showToast(
        "Error",
        error.response?.data?.message || "Failed to fetch blacklisted users",
        "error"
      );
    } finally {
      setLoader(false);
    }
  }, [currentPage, debouncedSearch, user.token]);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    fetchBlacklistedUsers();
  }, [fetchBlacklistedUsers]);

  // Handle unblock user
  const handleUnblock = async (userUuid) => {
    if (!window.confirm("Are you sure you want to unblock this user? They will regain access to the platform.")) {
      return;
    }

    setUnblockingId(userUuid);
    try {
      const { data } = await axios.delete(
        `${API.Users.app_blacklist_delete}/${userUuid}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      if (data.status === 200 && data.errorCode === 0) {
        showToast("Success", "User has been unblocked successfully", "success");
        // Remove from local state
        setBlacklistedUsers(prev => prev.filter(item => item.user?.uuid !== userUuid));
      } else {
        showToast("Error", data.message || "Failed to unblock user", "error");
      }
    } catch (error) {
      showToast("Error", error.response?.data?.message || "Failed to unblock user", "error");
    } finally {
      setUnblockingId(null);
    }
  };

  return (
    <div className="min-h-screen p-2 sl:pt-0 sm:p-2 md:p-4 sl:p-6">
      <div className="w-full max-w-full mx-auto mt-10 p-4 sm:p-8 md:p-12 bg-white dark:bg-gray-800 shadow-lg rounded-xl relative">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link
            to="/dashboard/users"
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700"
          >
            <FiArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-xl">
              <FiShieldOff className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                Blocked Users
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Manage users who have been blocked from the platform
              </p>
            </div>
          </div>
        </div>

        {/* Refresh Button */}
        <div className="absolute top-4 right-4 sm:top-8 sm:right-8">
          <button
            type="button"
            onClick={fetchBlacklistedUsers}
            disabled={loader}
            className="flex items-center justify-center text-primary bg-white dark:bg-gray-700 border border-primary rounded-full w-12 h-12 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
            title="Refresh Data"
          >
            <FiRefreshCw className={`w-5 h-5 ${loader ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Search Bar */}
        <div className="flex justify-end items-center border-b-2 border-b-[#DADADA] dark:border-gray-600 pb-4 mt-4">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white w-64"
            />
          </div>
        </div>

        {/* Loading State */}
        {loader && (
          <div className="flex justify-center items-center py-20">
            <Loader />
          </div>
        )}

        {/* Empty State */}
        {!loader && blacklistedUsers.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-20"
          >
            <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
              <FiShield className="w-12 h-12 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No Blocked Users
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-center max-w-md">
              There are no blocked users at the moment. All users have full access to the platform.
            </p>
          </motion.div>
        )}

        {/* Users Grid */}
        {!loader && blacklistedUsers.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <AnimatePresence>
              {blacklistedUsers.map((item, index) => (
                <motion.div
                  key={item.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5 border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow"
                >
                  {/* User Info */}
                  <div className="flex items-start gap-4">
                    <div className="relative">
                      <img
                        src={item.user?.image_url || '/default-avatar.png'}
                        alt={item.user?.name}
                        className="w-14 h-14 rounded-full object-cover border-2 border-red-200 dark:border-red-800"
                      />
                      <div className="absolute -bottom-1 -right-1 p-1 bg-red-500 rounded-full">
                        <FiShieldOff className="w-3 h-3 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                        {item.user?.name || 'Unknown User'}
                      </h3>
                      <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mt-1">
                        <FiMail className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate">{item.user?.email || 'N/A'}</span>
                      </div>
                      {item.user?.phone_number && (
                        <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                          <FiUser className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>{formatPhoneNumberForDisplay(item.user.phone_number)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Block Reason */}
                  <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <div className="flex items-start gap-2">
                      <FiAlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-red-700 dark:text-red-400 mb-1">
                          Reason for blocking:
                        </p>
                        <p className="text-sm text-red-600 dark:text-red-300">
                          {item.reason || "No reason provided"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Block Info */}
                  <div className="mt-3 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center gap-1.5">
                      <FiCalendar className="w-3.5 h-3.5" />
                      <span>Blocked: {item.created_at || 'N/A'}</span>
                    </div>
                    {item.blocked_by_user && (
                      <span>by {item.blocked_by_user.name}</span>
                    )}
                  </div>

                  {/* Unblock Button */}
                  {hasPermission(user, "update_user") && (
                    <button
                      onClick={() => handleUnblock(item.user?.uuid)}
                      disabled={unblockingId === item.user?.uuid}
                      className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {unblockingId === item.user?.uuid ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Unblocking...
                        </>
                      ) : (
                        <>
                          <FiShield className="w-4 h-4" />
                          Unblock User
                        </>
                      )}
                    </button>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Pagination */}
        {!loader && blacklistedUsers.length > 0 && (
          <div className="flex justify-end items-center mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default BlacklistPage;
